import { h, Fragment, FunctionComponent } from 'preact';
import { useEffect } from 'preact/hooks';
import { isIOS } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import SimpleButton from '~src/domains/common/components/Button';
import * as styles from '~src/styles/domains/accept/components/AutoPayModal.module.scss';
import { modalKeydownHandler } from '~src/utils/helpers';
import { trackPageMount } from '~src/utils/tracking';

type AutoPayModalProps = {
  isAutoPayModalVisible: boolean;
  setAutoPayModalVisible: Function;
  updateInputValue: (state: { name: string; value: any }) => void;
};

const AutoPayModal: FunctionComponent<AutoPayModalProps> = ({
  isAutoPayModalVisible,
  setAutoPayModalVisible,
  updateInputValue,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleDisableAutoPay = () => {
    setAutoPayModalVisible(false);
    dispatch(updateInputValue({ name: 'autopay', value: false }));
  };

  const handleKeepAutoPay = () => {
    setAutoPayModalVisible(false);
    dispatch(updateInputValue({ name: 'autopay', value: true }));
  };

  useEffect(() => {
    let keydownHandler: { (e: KeyboardEvent): void };

    if (isAutoPayModalVisible) {
      dispatch(trackPageMount('autopay-modal'));
      setTimeout(() => {
        const modalElement = document.querySelector<HTMLElement>(
          `.${styles['autopay-modal']} [tabindex="0"]`,
        );
        modalElement?.focus();
      }, 500);

      if (!isIOS) {
        keydownHandler = modalKeydownHandler(
          `.${styles['autopay-modal']}`,
          handleKeepAutoPay,
        );
        document.addEventListener('keydown', keydownHandler);
      }
    }

    return () => {
      if (keydownHandler)
        document.removeEventListener('keydown', keydownHandler);
    };
  }, [isAutoPayModalVisible]);

  return (
    <Fragment>
      <div
        className={styles['autopay-modal-backdrop']}
        tabIndex={-1}
        style={{
          display: isAutoPayModalVisible ? 'block' : 'none',
          position: 'fixed',
        }}
      >
        <div
          className={styles['autopay-modal-container']}
          aria-modal={true}
          role='dialog'
          aria-labelledby='autopay-modal-title'
          aria-hidden={false}
        >
          <div className={styles['autopay-modal']}>
            <div className={styles['autopay-modal-content-wrapper']}>
              <div
                className={styles['autopay-modal-header']}
                id='autopay-modal-title'
              >
                {t('accept:autopay.modalContent.header')}
              </div>
              <div className={styles['autopay-modal-text']}>
                {t('accept:autopay.modalContent.body')}
              </div>
              <div className={styles['autopay-modal-ctrls']}>
                <SimpleButton
                  classLabel='btn-primary'
                  label={t('accept:autopay.modalContent.keep')}
                  trackingLabel='keep-autopay'
                  onClickPost={handleKeepAutoPay}
                  trackClickPayload={{
                    label: t('accept:autopay.modalContent.keep'),
                  }}
                />
                <SimpleButton
                  classLabel='btn-reverse-primary'
                  label={t('accept:autopay.modalContent.disable')}
                  trackingLabel='disable-autopay'
                  onClickPost={handleDisableAutoPay}
                  trackClickPayload={{
                    label: t('accept:autopay.modalContent.disable'),
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AutoPayModal;
